import React, { useState, useEffect, useCallback, useMemo, Fragment, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import SvgIcon from "@mui/material/SvgIcon";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import {
  DataGrid,
  useGridRootProps,
  useGridApiContext,
  useGridSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridRowsProp,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
  GridCallbackDetails,
  GridFeatureMode,
  GridEventListener,
  GridRowHeightParams,
  GridRowHeightReturnValue,
} from "@mui/x-data-grid";

import moment from "moment";
import theme from "../../theme";
import {
  Copy2Icon,
  CrossIcon,
  Delete3Icon,
  EditIcon,
  FilterIcon,
  NotificationIcon,
  PlusIcon,
  SearchIcon,
} from "../../assets/icons";
import { checkIf, debounce, defaultFormat } from "../../utils";
import { Divider, Drawer, IconButton, InputAdornment, MenuItem, Select, TextField, Menu } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Can from "../Can";

interface IDataTableOptions {
  hideActions?: boolean;
  entityName?: string;
  permission?: { create?: string; edit?: string; delete?: string };
  hideToolbar?: boolean;
  hidePagination?: boolean;
  filter?: boolean;
}
interface IDataTableProps {
  rows: Array<any>;
  rowCount?: number;
  rowClick?: GridEventListener<"rowClick">;
  getRowNavigationUrl?: (rowId: any) => string;
  columns: GridColDef[];
  sortingMode?: GridFeatureMode;
  sortModel?: GridSortModel;
  sortChange?: (sortModel: GridSortModel) => void;
  paginationMode?: GridFeatureMode;
  paginationModel?: GridPaginationModel;
  paginationChange?: (model: GridPaginationModel, details: GridCallbackDetails<any>) => void;
  filterItems?: DataTableFilterItem[];
  filterModel?: DataTableFilter;
  filterModelChange?: (value: any) => void;
  loading?: boolean;
  edit?: (id: string) => void;
  create?: () => void;
  deleteRec?: (id: string) => void;
  copy?: (id: string) => void;
  options?: IDataTableOptions;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
}

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .ant-empty-img-1": {
    fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
  },
  "& .ant-empty-img-2": {
    fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
  },
  "& .ant-empty-img-3": {
    fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
  },
  "& .ant-empty-img-4": {
    fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
  },
  "& .ant-empty-img-5": {
    fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
    fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
  },
}));

const NoRowsOverlay = () => (
  <StyledGridOverlay>
    <svg style={{ flexShrink: 0 }} width="240" height="200" viewBox="0 0 184 152" aria-hidden focusable="false">
      <g fill="none" fillRule="evenodd">
        <g transform="translate(24 31.67)">
          <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
          <path
            className="ant-empty-img-1"
            d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
          />
          <path
            className="ant-empty-img-2"
            d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
          />
          <path
            className="ant-empty-img-3"
            d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
          />
        </g>
        <path
          className="ant-empty-img-3"
          d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
        />
        <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
          <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
          <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
        </g>
      </g>
    </svg>
    <Box sx={{ mt: 1 }}>No Data</Box>
  </StyledGridOverlay>
);

export const LoadingSkeleton = (props: any) => {
  return (
    <Box
      sx={{
        height: "max-content",
        backgroundColor: "#fff",
      }}
      className="skeleton-wrap"
    >
      {[...Array(props.length || 10)].map((_, key) => (
        <Fragment key={key}>
          <Box sx={{ display: "flex" }}>
            {props.cols.map((col: GridColDef, key: number) => (
              <Box key={`wave-${key}`} sx={{ minWidth: col.minWidth, flex: col.flex }}>
                <Skeleton
                  animation="wave"
                  variant="rounded"
                  sx={{ my: "21px", marginRight: "34px", marginLeft: "8px", height: 14, bgcolor: "rgb(33 34 37 / 4%)" }}
                />
              </Box>
            ))}
          </Box>
        </Fragment>
      ))}
    </Box>
  );
};

export interface DataTableFilter {
  [key: string]: any;
}

export interface DataTableFilterItem {
  field?: any;
  rangeField?: { min: string; max: string };
  label: string;
  type: string;
  listData?: Array<any>;
}

interface IEditBlock {
  entityname?: string;
  params?: any;
  edit?: Function;
  deleterec?: Function;
  copy?: Function;
}

export const GridCustomToolbar = React.forwardRef(function GridToolbar(props, ref) {
  const { className, create, filterItems, filterModel, filterModelChange, options, ...other } = props as any;
  const rootProps = useGridRootProps();

  const [showFilter, setShowFilter] = useState(false);
  const [innerFilterModel, setInnerFilterModel] = useState<DataTableFilter>(filterModel);

  if (rootProps.disableColumnFilter && rootProps.disableColumnSelector && rootProps.disableDensitySelector) {
    return null;
  }

  if (options.hideToolbar) {
    return null;
  }

  // console.log("innerFilterModel", innerFilterModel);

  const setField = (field: string, value: any) => {
    setInnerFilterModel((state: any) => ({
      ...state,
      [field]: value,
    }));
  };

  const clearFilterModel = () => {
    setInnerFilterModel({});
    filterModelChange((state: any) => ({
      query: state.query,
    }));
    setShowFilter(false);
  };

  const applyFilterModel = () => {
    filterModelChange((state: any) => ({
      ...state,
      ...innerFilterModel,
    }));

    setShowFilter(false);
  };

  const db = debounce((val: any) => {
    filterModelChange((state: any) => ({
      ...state,
      query: val,
    }));
  }, 500);

  const handleFilterChange = (value: any) => {
    db(value);
  };

  const hasAnyAppliedFilter = filterModel && Object.values(filterModel).some((x) => x != null && x !== "");

  return (
    <GridToolbarContainer
      sx={{ justifyContent: "flex-end", position: "absolute", right: "-26px", top: "-78px", gap: "16px" }}
      ref={ref}
      {...other}
    >
      {options.entityName !== "package" && options.filter ? (
        <TextField
          sx={{
            "& .MuiInputBase-root": {
              color: "text.dark2",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "normal",
              margin: 0,
              height: "44px",
              boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
              background: "#fff",
            },
            "& fieldset": { border: "none" },
          }}
          onChange={(e) => handleFilterChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color={theme.palette.text.dark2} />
              </InputAdornment>
            ),
          }}
          placeholder="Search by typing..."
        />
      ) : null}
      {options.entityName !== "package" ? (
        <Can permission={options.permission.create}>
          <Button
            onClick={create}
            color="primary"
            size="small"
            variant="contained"
            sx={{ display: "flex", padding: "10px 12px" }}
            startIcon={<PlusIcon />}
          >
            Create New {options.entityName}
          </Button>
        </Can>
      ) : null}

      {filterItems && (
        <>
          <IconButton
            onClick={() => setShowFilter(true)}
            sx={{
              "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
              "&:hover": { background: "#f9f9f9" },
              background: "#fff",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "normal",
              borderRadius: "8px",
              display: "flex",
              height: 44,
              padding: "10px 12px",
              "&, & svg": { color: "text.dark2" },
            }}
          >
            <FilterIcon height={20} width={20} />
            {hasAnyAppliedFilter && <NotificationIcon className="datatable-filter-notification-icon" />}
          </IconButton>
          <Drawer open={showFilter} anchor="right" onClose={() => setShowFilter(false)}>
            <Box sx={{ width: "450px" }}>
              <Box className="datatable-filter-header">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M8.33301 11.6667L3.33301 4.16667V2.5H16.6663V4.16667L11.6663 11.6667V16.6667L8.33301 18.3333V11.6667Z"
                    fill="#212225"
                  />
                </svg>
                <Box className="datatable-filter-header-text" sx={{ flex: "1 0 0" }}>
                  Filter
                </Box>
                <IconButton
                  aria-label="close"
                  onClick={() => setShowFilter(false)}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 16,
                    color: "text.fontLight2",
                  }}
                >
                  <CrossIcon height={20} width={20} />
                </IconButton>
              </Box>
              {filterItems?.map((filterItem: DataTableFilterItem, index: number) => (
                <Fragment key={index}>
                  {filterItem.type === "text" && (
                    <>
                      <Divider />
                      <Box className="datatable-filter-item">
                        <Box className="datatable-filter-item-label">{filterItem.label}</Box>
                        <Box className="datatable-filter-item-numeric-range-group">
                          <Box className="datatable-filter-item-numeric-range-item">
                            <TextField
                              sx={{ width: "100%" }}
                              className="form-field"
                              value={innerFilterModel[filterItem.field]}
                              onChange={(e) => setField(filterItem.field, e.target.value)}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                  {filterItem.type === "select" && (
                    <>
                      <Divider />
                      <Box className="datatable-filter-item">
                        <Box className="datatable-filter-item-label">{filterItem.label}</Box>
                        <Box className="datatable-filter-item-numeric-range-group">
                          <Box className="datatable-filter-item-numeric-range-item">
                            <Select
                              sx={{ width: "100%" }}
                              value={innerFilterModel[filterItem.field] || null}
                              onChange={(e) => setField(filterItem.field, e.target.value)}
                            >
                              <MenuItem key="empty">Choose</MenuItem>
                              {filterItem.listData?.map((item) => {
                                if (filterItem.field === "area") {
                                  return (
                                    <MenuItem key={item.id} value={item.name}>
                                      {item.name}
                                    </MenuItem>
                                  );
                                }

                                if (typeof item === "object") {
                                  return (
                                    <MenuItem key={item.id} value={item.code || item.id}>
                                      {item.fullName || item.code || item.name}
                                    </MenuItem>
                                  );
                                } else {
                                  return (
                                    <MenuItem key={item} value={item}>
                                      {item}
                                    </MenuItem>
                                  );
                                }
                              })}
                            </Select>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                  {filterItem.type === "numericRange" && (
                    <>
                      <Divider />
                      <Box className="datatable-filter-item">
                        <Box className="datatable-filter-item-label">{filterItem.label}</Box>
                        <Box className="datatable-filter-item-numeric-range-group">
                          <Box className="datatable-filter-item-numeric-range-item">
                            <Box className="datatable-filter-item-numeric-range-item-header">Min</Box>
                            <Box>
                              <TextField
                                className="form-field"
                                value={innerFilterModel[filterItem.rangeField!.min]}
                                onChange={(e) => setField(filterItem.rangeField!.min, e.target.value)}
                              />
                            </Box>
                          </Box>
                          <Box className="datatable-filter-item-numeric-range-item">
                            <Box className="datatable-filter-item-numeric-range-item-header">Max</Box>
                            <Box>
                              <TextField
                                className="form-field"
                                value={innerFilterModel[filterItem.rangeField!.max]}
                                onChange={(e) => setField(filterItem.rangeField!.max, e.target.value)}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  )}
                  {filterItem.type === "dateRange" && (
                    <>
                      <Divider />
                      <Box className="datatable-filter-item">
                        <Box className="datatable-filter-item-label">{filterItem.label}</Box>
                        <Box className="datatable-filter-item-date-range-item">
                          <DatePicker
                            value={
                              innerFilterModel &&
                              innerFilterModel[filterItem.rangeField!.min] &&
                              moment(innerFilterModel[filterItem.rangeField!.min])
                            }
                            onChange={(date) =>
                              setField(filterItem.rangeField!.min, date && moment(date).format(defaultFormat))
                            }
                            format="DD/MM/YYYY"
                            slotProps={{
                              field: { clearable: true },
                            }}
                          />
                          <DatePicker
                            value={
                              innerFilterModel &&
                              innerFilterModel[filterItem.rangeField!.max] &&
                              moment(innerFilterModel[filterItem.rangeField!.max])
                            }
                            onChange={(date) =>
                              setField(filterItem.rangeField!.max, date && moment(date).format(defaultFormat))
                            }
                            format="DD/MM/YYYY"
                            slotProps={{
                              field: { clearable: true },
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  )}
                </Fragment>
              ))}
              <Box className="datatable-filter-action">
                <Button size="small" onClick={() => clearFilterModel()} sx={{ display: "flex", flex: "1 0 0" }}>
                  Clear
                </Button>
                <Button
                  color="secondary"
                  size="small"
                  variant="contained"
                  onClick={() => applyFilterModel()}
                  sx={{ display: "flex", flex: "1 0 0" }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Drawer>
        </>
      )}
    </GridToolbarContainer>
  );
});

export const CustomSwitch = React.forwardRef(function (props: any, ref) {
  const { className, children, ...other } = props;
  return <Checkbox ref={ref} {...other} />;
});

export const CustomColumnsPanel = React.forwardRef(function (props: any, ref) {
  const { className, children, ...other } = props;
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const [open, setOpen] = React.useState(true);
  const dispatch = useDispatch();
  const premiumFields = ["insiderNetValueTraded", "netProfit", "totalDebt"];

  const hidePreferences = () => {
    apiRef.current.hidePreferences();
  };
  const handleClose = () => {
    setOpen(false);
  };
  const toggleColumn = (event: any) => {
    // if (session) {
    //   const { name: field } = event.target;
    //   const subscription = session.user.subscription;
    //   if (premiumFields.includes(field) && subscription === "SAHMIK_SUBSCRIBER") {
    //     dispatch({ type: "SET_SUBSCRIBER_UPGRADE_MODAL", payload: true });
    //   } else {
    //     apiRef.current.setColumnVisibility(field, columnVisibilityModel[field] === false);
    //   }
    // } else {
    //   hidePreferences();
    //   dispatch({ type: "SET_SIGN_IN_MODAL", payload: true });
    // }
  };

  return (
    <div className="MuiDataGrid-panelWrapper" style={{ width: "100%" }}>
      <Box
        className="MuiDataGrid-panelHeader"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #ECECED",
        }}
        p={4}
      >
        <Typography component="span" sx={{ color: "text.D800", fontSize: "17px", lineHeight: "27px", fontWeight: 700 }}>
          Metrics to Display
        </Typography>
        <CloseIcon onClick={hidePreferences} fontSize="small" sx={{ color: "text.D50", cursor: "pointer" }} />
      </Box>
      <Box className="MuiDataGrid-panelContent" sx={{ overflow: "auto", maxHeight: "calc(100% - 60px)" }}>
        <div className="MuiDataGrid-columnsPanel">
          {columns.map((column) => {
            return column.hideable === false ? null : (
              <div className="MuiDataGrid-columnsPanelRow" key={column.field}>
                <Typography
                  mt={4}
                  ml={4}
                  mr={4}
                  component="label"
                  sx={[
                    { display: "flex", alignItems: "center", justifyContent: "space-between" },
                    { "&:last-child": { marginBottom: theme.spacing(4) } },
                  ]}
                >
                  <span>
                    <CustomSwitch
                      checked={columnVisibilityModel[column.field] !== false}
                      onClick={toggleColumn}
                      name={column.field}
                      size="small"
                      sx={{ padding: 0 }}
                    />
                    <span style={{ cursor: "pointer", marginLeft: "8px" }} className="MuiTypography-root">
                      {column.headerName || column.field}
                    </span>
                  </span>
                  <span>{column.headerName}</span>
                </Typography>
              </div>
            );
          })}
        </div>
      </Box>
    </div>
  );
});

export const CustomUnsortedIcon = (props: any) => {
  return (
    <SvgIcon width="24" height="33" viewBox="0 0 24 33">
      <path d="M7 14L12 9L17 14H7Z" fill="#6D7178" />
      <path d="M17 18L12 23L7 18L17 18Z" fill="#6D7178" />
    </SvgIcon>
  );
};

export const CustomArrowDropDownIcon = (props: any) => {
  return (
    <SvgIcon width="24" height="33" viewBox="0 0 24 33">
      <path d="M7 14L12 9L17 14H7Z" fill="#6D7178" />
      <path d="M17 18L12 23L7 18L17 18Z" fill="#c0c2c8" />
    </SvgIcon>
  );
};
export const CustomArrowDropUpIcon = (props: any) => {
  return (
    <SvgIcon width="24" height="33" viewBox="0 0 24 33">
      <path d="M7 14L12 9L17 14H7Z" fill="#c0c2c8" />
      <path d="M17 18L12 23L7 18L17 18Z" fill="#6D7178" />
    </SvgIcon>
  );
};

export const DeleteBlock = React.forwardRef((props: IEditBlock, ref: any) => {
  const { entityname, params, deleterec, ...rest } = props;
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        if (entityname === "payment" && params.row.editable === false) {
          return;
        } else {
          deleterec && deleterec(params);
        }
      }}
      {...rest}
      ref={ref}
      sx={{
        cursor: `${entityname === "payment" && params.row.editable === false ? "not-allowed" : "pointer"}`,
        height: "32px",
        padding: "8px",
        borderRadius: "4px",
        background: "rgba(226, 64, 64, 0.08)",
        opacity: `${entityname === "payment" && params.row.editable === false ? "0.5" : "1.0"}`,
      }}
    >
      <Delete3Icon
        style={{
          cursor: `${entityname === "payment" && params.row.editable === false ? "not-allowed" : "pointer"}`,
        }}
        width={16}
        height={16}
        color={theme.palette.text.style}
      />
    </Box>
  );
});

export const EditBlock = React.forwardRef((props: IEditBlock, ref: any) => {
  const { entityname, params, edit, ...rest } = props;
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        if (entityname === "payment" && params.row.editable === false) {
          return;
        } else {
          edit && edit(params);
        }
      }}
      {...rest}
      ref={ref}
      sx={{
        cursor: `${entityname === "payment" && params.row.editable === false ? "not-allowed" : "pointer"}`,
        height: "32px",
        padding: "8px",
        borderRadius: "4px",
        background: "rgba(19, 111, 215, 0.08)",
        opacity: `${entityname === "payment" && params.row.editable === false ? "0.5" : "1.0"}`,
      }}
    >
      <EditIcon
        style={{
          cursor: `${entityname === "payment" && params.row.editable === false ? "not-allowed" : "pointer"}`,
        }}
        width={16}
        height={16}
        color={theme.palette.text.blue}
      />
    </Box>
  );
});

export const CopyBlock = React.forwardRef((props: IEditBlock, ref: any) => {
  const { params, copy, ...rest } = props;
  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        copy && copy(params);
      }}
      {...rest}
      ref={ref}
      sx={{
        cursor: "pointer",
        height: "32px",
        padding: "8px",
        borderRadius: "4px",
        background: "rgba(255, 143, 11, 0.08)",
      }}
    >
      <Copy2Icon width={16} height={16} color={theme.palette.text.orange} />
    </Box>
  );
});

export default function DataTable({
  rows,
  rowCount,
  rowClick,
  getRowNavigationUrl,
  columns,
  sortingMode,
  sortModel,
  sortChange,
  paginationMode,
  paginationModel,
  paginationChange,
  filterItems,
  filterModel,
  filterModelChange,
  loading,
  create,
  edit,
  deleteRec,
  copy,
  getRowHeight,
  options,
}: IDataTableProps) {
  const dispatch = useDispatch();
  const [rowsData, setRowsData] = useState(rows);
  const ref = useRef<any>();
  const [visible, setVisible] = useState(false);

  // Convert rows to link-like behavior for native browser interactions
  useEffect(() => {
    if (!getRowNavigationUrl) return;

    const gridRoot = ref.current?.querySelector('.MuiDataGrid-virtualScroller');
    if (!gridRoot) return;

    const updateRowLinks = () => {
      const rowElements = gridRoot.querySelectorAll('.MuiDataGrid-row');
      rowElements.forEach((rowDiv: any) => {
        const rowId = rowDiv.getAttribute('data-id');
        if (!rowId) return;

        const url = getRowNavigationUrl(rowId);

        // Check if we've already wrapped this row
        if (rowDiv.querySelector('.row-link-wrapper')) return;

        // Create an anchor element that wraps the row content
        const cells = rowDiv.querySelectorAll('.MuiDataGrid-cell');
        cells.forEach((cell: HTMLElement) => {
          // Skip if already has a wrapper, is an action column, or contains action buttons
          if (cell.querySelector('.row-link-wrapper') ||
              cell.querySelector('[class*="Icon"]') ||
              cell.getAttribute('data-field') === 'actions') return;

          const wrapper = document.createElement('a');
          wrapper.href = url;
          wrapper.className = 'row-link-wrapper';
          wrapper.style.cssText = 'display: flex; align-items: center; width: 100%; height: 100%; color: inherit; text-decoration: none;';

          // Move cell content into wrapper
          while (cell.firstChild) {
            wrapper.appendChild(cell.firstChild);
          }

          cell.appendChild(wrapper);
        });
      });
    };

    updateRowLinks();

    // Update links when rows change
    const observer = new MutationObserver(updateRowLinks);
    observer.observe(gridRoot, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [getRowNavigationUrl, rows]);

  let populatedColumns = [...columns];

  if (!options?.hideActions) {
    populatedColumns.push({
      field: "actions",
      sortable: false,
      hideable: false,
      minWidth: 128,
      flex: 0,
      headerName: "Actions",
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", columnGap: "8px" }}>
          {checkIf(edit) ? (
            <Can permission={options?.permission?.edit as string}>
              <EditBlock entityname={options?.entityName} params={params} edit={edit} />
            </Can>
          ) : null}
          {checkIf(deleteRec) ? (
            <Can permission={options?.permission?.delete as string}>
              <DeleteBlock entityname={options?.entityName} params={params} deleterec={deleteRec} />
            </Can>
          ) : null}
          {checkIf(copy) ? (
            <Can permission={options?.permission?.create as string}>
              <CopyBlock entityname={options?.entityName} params={params} copy={copy} />
            </Can>
          ) : null}
        </Box>
      ),
    });
  }

  const defaultColumnVisibilityModel = useMemo(
    () => ({
      companyNameCombined: true,
      symbol: true,
      companyName: true,
      currentPrice: true,
      change: true,
      changePercentage: true,
      eps: false,
      equity: false,
      dividendPayout: false,
      dividendYield: false,
      cutoffDate: false,
      bookValue: false,
      pbRatio: false,
      peRatio: false,
      percentReturn: false,
      returnOnAssets: false,
      returnOnEquity: false,
      sharesOutstanding: false,
      openPrice: true,
      previousClosePrice: false,
      lowPrice: false,
      highPrice: false,
      offerPrice: true,
      bidPrice: true,
      offerVolume: false,
      bidVolume: false,
      marketCap: true,
      numberOfTrades: false,
      volumeTraded: true,
      volumeTradedAvg30: false,
      valueTraded: true,
      ltmValueTraded: false,
      insiderNetValueTraded: false,
      netProfit: false,
      totalDebt: false,
      week52High: false,
      week52Low: false,
      sparkline: false,
    }),
    []
  );

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultColumnVisibilityModel);

  return (
    <>
      <DataGrid
        ref={ref}
        autoHeight
        rowHeight={45}
        columnHeaderHeight={36}
        disableRowSelectionOnClick={true}
        rowSelection={false}
        checkboxSelection={false}
        disableColumnMenu={true}
        getRowHeight={getRowHeight}
        getRowClassName={(params) => `data-grid-row`}
        getRowId={(row) => row.id}
        localeText={{ toolbarColumns: "" }}
        loading={loading}
        rows={rows}
        rowCount={rowCount}
        onRowClick={rowClick}
        columns={populatedColumns}
        sortingMode={sortingMode || "client"}
        sortModel={sortModel}
        onSortModelChange={sortChange}
        paginationMode={options?.hidePagination ? undefined : paginationMode || "client"}
        paginationModel={options?.hidePagination ? undefined : paginationModel}
        onPaginationModelChange={options?.hidePagination ? undefined : paginationChange}
        pageSizeOptions={[5, 10, 25, 50]}
        slots={{
          toolbar: GridCustomToolbar,
          columnsPanel: CustomColumnsPanel,
          columnUnsortedIcon: CustomUnsortedIcon,
          columnSortedAscendingIcon: CustomArrowDropUpIcon,
          columnSortedDescendingIcon: CustomArrowDropDownIcon,
          loadingOverlay: LoadingSkeleton,
          noRowsOverlay: NoRowsOverlay,
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => {
          //   setColumnVisibilityModel(newModel);
        }}
        onPreferencePanelOpen={(params, event, details) => {
          document.body.classList.add("body-lock");
          document.getElementsByClassName("main-navbar-large-con")[0].classList.add("header-lock-fix");
        }}
        onPreferencePanelClose={(params, event, details) => {
          setTimeout(() => {
            document.body.classList.remove("body-lock");
            document.getElementsByClassName("main-navbar-large-con")[0].classList.remove("header-lock-fix");
          }, 300);
        }}
        sx={{
          width: "100%",
          "--DataGrid-overlayHeight": "300px",
          borderColor: theme.palette.text.tableColor,
          borderRadius: "8px",
          "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
            padding: "0 8px",
          },
          "& .MuiDataGrid-columnHeaders": {
            borderTopLeftRadius: "6px",
            borderTopRightRadius: "6px",
            borderColor: [theme.palette.text.tableColor],
            backgroundColor: [theme.palette.text.tableColor],
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "12px",
            color: theme.palette.text.dark,
          },
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-toolbarContainer": {
            padding: 0,
          },
          "& .MuiDataGrid-cell:focus-within,& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
            {
              outline: "none",
            },
          "& .MuiDataGrid-row": {
            borderTopStyle: "solid",
            borderTopWidth: "1px",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            borderColor: theme.palette.text.tableColor,
            backgroundColor: theme.palette.text.white,
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            borderColor: theme.palette.text.tableColor,
            backgroundColor: theme.palette.text.white,
          },
          "& .MuiDataGrid-row:last-child": {
            borderBottomRightRadius: "8px",
            borderBottomLeftRadius: "8px",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#FCFCFD",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {},
          "& .MuiDataGrid-cell": {
            color: theme.palette.text.dark2,
            fontSize: "14px",
            fontWeight: 500,
            borderRight: "1px solid",
            borderColor: theme.palette.text.tableColor,
          },
          "& .MuiDataGrid-row .MuiDataGrid-cell:last-of-type": {
            borderRight: "none",
          },
          "& .MuiDataGrid-cell:hover": {},
          "& .MuiDataGrid-cell svg": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {},
          "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer > button": {
            padding: 0,
            visibility: "visible",
          },
          "& .MuiDataGrid-withBorderColor": {
            borderColor: theme.palette.text.tableColor,
          },
        }}
        slotProps={{
          loadingOverlay: { length: rows.length, cols: populatedColumns },
          toolbar: { create, options, filterItems, filterModel, filterModelChange },
          panel: {
            sx: [
              {
                position: "fixed !important",
                top: "0 !important",
                left: "0 !important",
                right: "0 !important",
                bottom: "0 !important",
                transform: "none !important",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
              {
                "& > div": {
                  maxHeight: "unset",
                  height: "100%",
                },
                "& .MuiDataGrid-paper": { borderRadius: 0, width: ["100%", "300px"], position: "absolute", right: 0 },
                "& .MuiDataGrid-columnsPanelRow .MuiTypography-root": {
                  color: theme.palette.text.fontLight2,
                  fontWeight: 500,
                  fontSize: "14px",
                },
                "& .MuiDataGrid-columnsPanelRow .Mui-checked + .MuiTypography-root, & .MuiDataGrid-columnsPanelRow .MuiCheckbox-root.Mui-checked":
                  {
                    color: theme.palette.text.fontLight2,
                  },
                "& .MuiDataGrid-columnsPanelRow .MuiCheckbox-root": {
                  color: theme.palette.text.fontLight4,
                },
              },
            ],
          },
          footer: {
            sx: [
              {
                "&": {
                  //   border: "none",
                  //   position: "absolute",
                },
              },
            ],
          },
        }}
      />
    </>
  );
}
